<template>
  <div class="app">
    <!-- ЭКРАН ВХОДА -->
    <div class="login-wrap" v-if="!isAuth">
      <div class="login">
        <img
          class="login__logo"
          src="https://sadkomed.ru/files/7beea2d1-2d66-4820-b601-2ba0f6559f44.png"
          alt="Садко"
        />
        <h1 class="login__title">Чат с врачом</h1>
        <p class="login__hint">
          Войдите по номеру телефона, на который вы записывались в клинику.
        </p>

        <p class="login__error" v-if="error" role="alert">{{ error }}</p>

        <label class="lbl" for="phone">Номер телефона</label>
        <input
          id="phone"
          class="input"
          type="tel"
          inputmode="tel"
          autocomplete="tel"
          placeholder="+7 900 000 00 00"
          v-model="phone"
          v-mask="'+7 9## ### ####'"
          :disabled="code !== null"
          @input="clearError"
          @keyup.enter="handlePhoneEnter"
        />

        <div class="pin" v-if="code !== null">
          <label class="lbl">Код из SMS</label>
          <PincodeInput v-model="code" placeholder="" />
          <div class="pin__actions">
            <button
              type="button"
              class="linkbtn"
              :disabled="resendIn > 0 || loadingPhone"
              @click="resendCode"
            >
              {{ resendIn > 0 ? `Отправить повторно через ${resendIn} с` : 'Отправить код повторно' }}
            </button>
            <button type="button" class="linkbtn" @click="changeNumber">Изменить номер</button>
          </div>
        </div>

        <button class="btn btn--primary" @click="auth" :disabled="disabledLogin">
          {{ authBtn }}
        </button>

        <button type="button" class="linkbtn login__about" @click="showInfo = !showInfo">
          {{ showInfo ? 'Скрыть' : 'О сервисе' }}
        </button>
        <ChatInfo v-if="showInfo" class="login__info" />
      </div>
    </div>

    <!-- ОСНОВНОЙ ЭКРАН -->
    <template v-else>
      <div class="app-loading" v-if="loading">
        <span class="spinner spinner--lg"></span>
        <p>Загружаем ваши чаты…</p>
      </div>

      <div class="layout" v-else>
        <!-- Сайдбар -->
        <aside class="sidebar" :class="{ 'is-hidden-mobile': currentChatId > 0 }">
          <div class="sidebar__head">
            <img
              class="sidebar__logo"
              src="https://sadkomed.ru/files/7beea2d1-2d66-4820-b601-2ba0f6559f44.png"
              alt="Садко"
            />
            <button type="button" class="linkbtn" @click="confirmExit">
              <LogoutIcon /> Выйти
            </button>
          </div>

          <div class="sidebar__scroll">
            <div class="profiles" v-if="profiles.length > 1">
              <span class="lbl lbl--section">Пациент</span>
              <button
                type="button"
                class="profile"
                :class="{ 'profile--active': isProfileSelected(profile) }"
                v-for="profile in profiles"
                :key="profile.id"
                @click="selectProfile(profile)"
              >
                {{ profile.name }}
              </button>
            </div>

            <div class="search" v-if="chatsList.length > SEARCH_THRESHOLD">
              <SearchIcon class="search__icon" />
              <input
                class="input input--search"
                type="search"
                placeholder="Поиск по имени"
                aria-label="Поиск по имени"
                v-model="search"
              />
            </div>

            <span class="lbl lbl--section">Чаты</span>

            <div class="chat-list">
              <p class="chat-list__empty" v-if="!hasChats">
                {{ search ? 'Ничего не найдено' : 'Активных чатов пока нет' }}
              </p>
              <ChatPreview
                v-for="chat in filteredChats"
                :key="chat.id"
                :chat="chat"
                :current-chat-id="currentChatId"
                :current-profile-id="currentProfile ? currentProfile.id : null"
                :select="selectChat"
              />
            </div>
          </div>

          <div class="sidebar__foot">
            <span class="conn" v-if="isOffline">
              <span class="conn__dot"></span> Нет связи, переподключаемся…
            </span>
            <span class="sidebar__test">Сервис работает в режиме тестирования</span>
          </div>
        </aside>

        <!-- Область чата -->
        <section class="chat" v-if="currentChatId > -1">
          <header class="chat__head">
            <button class="ghost-btn chat__back" aria-label="Назад к списку чатов" @click="clearChat">
              <ArrowLeftIcon />
            </button>
            <span class="avatar avatar--sm">
              <img v-if="currentChatPhoto" :src="currentChatPhoto" alt="" />
              <template v-else>{{ initials(currentChatUserNames) }}</template>
            </span>
            <div class="chat__meta">
              <div class="chat__name">{{ currentChatUserNames || 'Чат' }}</div>
              <div class="chat__status">
                <template v-if="isCurrentChatClosed">Чат приостановлен</template>
                <template v-else-if="isOffline">Нет связи</template>
                <template v-else>Врач ответит в течение 24 часов</template>
              </div>
            </div>
            <span class="spinner" v-show="isLoadingMessages && !messages.length"></span>
          </header>

          <DragAndDropUploader class="chat__body" @add="addFiles" :disabled="isCurrentChatClosed">
            <div class="messages" ref="messages" @scroll="onMessagesScroll">
              <p class="messages__empty" v-if="!messageStream.length && !isLoadingMessages">
                Напишите первое сообщение — врач ответит в течение 24 часов.
              </p>
              <template v-for="item in messageStream">
                <div class="day" v-if="item.kind === 'day'" :key="item.id">
                  <span>{{ formatDayLabel(item.value) }}</span>
                </div>
                <ChatMessage
                  v-else
                  :key="item.id"
                  :message="item.message"
                  :first-of-group="item.firstOfGroup"
                  :last-of-group="item.lastOfGroup"
                  @seen="markSeen(item.message)"
                  @retry="retryPending(item.message)"
                />
              </template>
            </div>

            <transition name="fade">
              <button class="jump" v-if="hasNewBelow" @click="scrollToBottom(true)">
                Новые сообщения ↓
              </button>
            </transition>

            <div class="composer" :class="{ 'composer--disabled': isCurrentChatClosed }">
              <div class="composer__files" v-if="files.length">
                <div class="attach" v-for="(file, index) in files" :key="file.uid">
                  <img class="attach__thumb" v-if="file.preview" :src="file.preview" alt="" />
                  <span class="attach__ext" v-else>{{ fileExt(file.name) }}</span>
                  <span class="attach__meta">
                    <span class="attach__name">{{ file.name }}</span>
                    <span class="attach__size">{{ formatFileSize(file.size) }}</span>
                  </span>
                  <button type="button" class="attach__x" aria-label="Убрать файл" @click="removeFile(index)">×</button>
                </div>
              </div>

              <div class="composer__row">
                <label class="ghost-btn" :class="{ 'is-disabled': isSendDisabled }" aria-label="Прикрепить файл">
                  <AttachmentIcon />
                  <input
                    class="visually-hidden"
                    type="file"
                    ref="fileInput"
                    multiple
                    :disabled="isSendDisabled"
                    @change="onFileInput"
                  />
                </label>

                <textarea
                  ref="input"
                  class="composer__input"
                  v-model="message"
                  rows="1"
                  :placeholder="isCurrentChatClosed ? 'Чат приостановлен' : 'Сообщение…'"
                  :disabled="isCurrentChatClosed"
                  aria-label="Текст сообщения"
                  @input="autogrow"
                  @keydown.enter.exact.prevent="send"
                ></textarea>

                <button
                  class="send-btn"
                  aria-label="Отправить"
                  :disabled="!canSend"
                  @click="send"
                >
                  <span v-if="uploadPercent > -1" class="send-btn__pct">{{ uploadPercent }}%</span>
                  <SendIcon v-else />
                </button>
              </div>
            </div>
          </DragAndDropUploader>
        </section>

        <div class="chat chat--empty is-hidden-mobile" v-else>
          <div class="empty">
            <p class="empty__title">Выберите чат</p>
            <p>Здесь появится переписка с врачом.</p>
          </div>
        </div>
      </div>
    </template>

    <Toast />
  </div>
</template>

<script>
import api, { uploadApi } from './api';
import WebSocketService from './WebSocketService';
import { MAX_FILE_SIZE, MAX_FILE_SIZE_LABEL } from './constants';
import { toast } from './toast';
import { buildMessageStream } from './utils/messageStream';
import { formatDayLabel } from './utils/datetime';
import { initials } from './utils/text';

import ChatMessage from './ChatMessage.vue';
import ChatPreview from './ChatPreview.vue';
import ChatInfo from './components/ChatInfo.vue';
import Toast from './components/Toast.vue';
import PincodeInput from 'vue-pincode-input';
import ArrowLeftIcon from './components/ArrowLeftIcon.vue';
import AttachmentIcon from './components/AttachmentIcon.vue';
import LogoutIcon from './components/LogoutIcon.vue';
import SearchIcon from './components/SearchIcon.vue';
import SendIcon from './components/SendIcon.vue';
import DragAndDropUploader from './components/DragAndDropUploader.vue';

const CONFIG = {
  SEARCH_THRESHOLD: 5,
  RELOAD_DEBOUNCE: 250,
  SEND_TIMEOUT: 15000,
  SOUND_THROTTLE: 3000,
  PHONE_LENGTH: 11,
  RESEND_COOLDOWN: 30,
};

const STORAGE_KEYS = { TOKEN: 'token', SALT: 'salt', PHONE: 'phone' };

const storage = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set(key, value) {
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    } catch (e) {
      /* приватный режим и т.п. */
    }
  },
  clear(...keys) {
    keys.forEach((k) => this.set(k, null));
  },
};

function cleanPhoneNumber(phone) {
  return (phone || '').replace(/\D/g, '');
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

function fileExt(name) {
  const parts = String(name || '').split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase().slice(0, 4) : 'FILE';
}

function isChatClosed(chat) {
  if (!chat || !chat.deleted) return false;
  const t = new Date(chat.deleted).getTime();
  return !isNaN(t) && Date.now() > t;
}

function sortByCreatedAsc(items) {
  return [...items].sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
}

let uidSeq = 0;

export default {
  name: 'Chats',

  components: {
    ChatMessage,
    ChatPreview,
    ChatInfo,
    Toast,
    PincodeInput,
    ArrowLeftIcon,
    AttachmentIcon,
    LogoutIcon,
    SearchIcon,
    SendIcon,
    DragAndDropUploader,
  },

  data() {
    return {
      loading: true,
      loadingPhone: false,
      loadingCode: false,
      isOffline: true,

      phone: '',
      code: null,
      error: '',
      showInfo: false,
      resendIn: 0,

      profiles: [],
      currentProfile: null,
      chats: [],
      currentChatId: -1,

      messages: [],
      pending: [],
      messagesSeen: [],
      isLoadingMessages: false,

      message: '',
      files: [],
      uploadPercent: -1,
      isSending: false,

      search: '',
      hasNewBelow: false,

      SEARCH_THRESHOLD: CONFIG.SEARCH_THRESHOLD,
      MAX_FILE_SIZE_LABEL,
    };
  },

  computed: {
    isAuth() {
      const hasToken = storage.get(STORAGE_KEYS.TOKEN) !== null;
      const hasSalt = storage.get(STORAGE_KEYS.SALT) !== null;
      return (hasToken && hasSalt) || this.profiles.length > 0;
    },

    clearedPhone() {
      return cleanPhoneNumber(this.phone);
    },

    disabledLogin() {
      if (this.loadingCode || this.loadingPhone) return true;
      if (this.code === null) {
        return this.clearedPhone.length !== CONFIG.PHONE_LENGTH || this.error.length > 0;
      }
      return !this.code || this.code.length === 0;
    },

    authBtn() {
      if (this.loadingPhone) return 'Отправляем…';
      if (this.loadingCode) return 'Проверяем…';
      return this.code === null ? 'Получить код' : 'Войти';
    },

    chatsList() {
      const withMessages = this.chats.filter((c) => c.messages && c.messages.length > 0);
      return [...withMessages].sort(
        (a, b) => new Date(b.messages[0].created).getTime() - new Date(a.messages[0].created).getTime()
      );
    },

    filteredChats() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.chatsList;
      return this.chatsList.filter((c) =>
        (c.users || []).some((u) => (u.name || '').toLowerCase().includes(q))
      );
    },

    hasChats() {
      return this.filteredChats.length > 0;
    },

    currentChat() {
      return this.chats.find((c) => c.id === this.currentChatId) || null;
    },

    isCurrentChatClosed() {
      return isChatClosed(this.currentChat);
    },

    currentChatPhoto() {
      const chat = this.currentChat;
      if (!chat || !chat.users) return null;
      const doctor = chat.users.find((u) => u.type === 'doctor');
      if (!doctor || !doctor.media) return null;
      return `https://lk.sadkomed.ru/customer-assets/media/${doctor.media}/small`;
    },

    currentChatUserNames() {
      const chat = this.currentChat;
      if (!chat || !chat.users || !this.currentProfile) return '';
      return chat.users
        .filter((u) => u.id !== this.currentProfile.id)
        .map((u) => u.name)
        .filter(Boolean)
        .join(', ');
    },

    visibleMessages() {
      const real = this.messages.filter((m) => !m.remark || m.remark.length === 0);
      const shownPending = this.pending.filter((p) => !this.isPendingMatched(p));
      return sortByCreatedAsc([...real, ...shownPending]);
    },

    messageStream() {
      return buildMessageStream(this.visibleMessages);
    },

    unreadTotal() {
      if (!this.currentProfile) return 0;
      const pid = this.currentProfile.id;
      return this.chatsList.reduce((sum, c) => {
        const last = c.messages && c.messages[0];
        if (!last || !last.user || last.user.id === pid) return sum;
        const seenIds = (last.seen || []).map((s) => s.id);
        return seenIds.includes(pid) ? sum : sum + 1;
      }, 0);
    },

    isSendDisabled() {
      return this.isOffline || this.isCurrentChatClosed;
    },

    canSend() {
      if (this.isSendDisabled || this.isSending) return false;
      return this.message.trim().length > 0 || this.files.length > 0;
    },
  },

  watch: {
    currentProfile(next, prev) {
      this.currentChatId = -1;
      this.messages = [];
      this.pending = [];
      if (prev) this.ws.unsubscribe(`/patient/${prev.uid}`);
      if (next) {
        this.ws.subscribe(`/patient/${next.uid}`, this.onSocketChat);
        this.loadChats();
      }
    },

    unreadTotal(n) {
      document.title = n > 0 ? `(${n}) Садко — Чаты` : 'Садко — Чаты';
    },
  },

  created() {
    this.ws = new WebSocketService();
    this.ws.on('connect', this.onSocketConnect);
    this.ws.on('disconnect', this.onSocketDisconnect);

    this._reloadTimer = null;
    this._reloadQueued = false;
    this._sound = null;
    this._lastSoundAt = 0;
    this._resendTimer = null;
  },

  mounted() {
    this.initViewportHeight();
    this.initFromStorage();
    this.initAuth();

    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    document.addEventListener('visibilitychange', this.handleVisibility);
  },

  beforeDestroy() {
    this.cleanup();
  },

  methods: {
    formatDayLabel,
    formatFileSize,
    fileExt,
    initials,

    // ---------- инициализация ----------
    initViewportHeight() {
      const setVH = () => {
        document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
      };
      setVH();
      this._resizeHandler = setVH;
      window.addEventListener('resize', this._resizeHandler);
    },

    initFromStorage() {
      const savedPhone = storage.get(STORAGE_KEYS.PHONE);
      if (savedPhone) this.phone = savedPhone;
    },

    async initAuth() {
      const result = await this.validateToken();
      if (result === 'ok') {
        this.ws.connect();
      } else if (result === 'invalid') {
        this.exit();
      } else {
        this.loading = false;
        this.isOffline = true;
        this.ws.connect();
        if (this._authRetry) clearTimeout(this._authRetry);
        this._authRetry = setTimeout(() => {
          if (!this.profiles.length) this.initAuth();
        }, 5000);
      }
    },

    cleanup() {
      if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
      document.removeEventListener('visibilitychange', this.handleVisibility);
      if (this._reloadTimer) clearTimeout(this._reloadTimer);
      if (this._resendTimer) clearInterval(this._resendTimer);
      if (this._authRetry) clearTimeout(this._authRetry);
      this.files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
      this.ws.disconnect();
    },

    // ---------- сеть / сокет ----------
    handleOnline() {
      this.ws.reconnectNow();
    },
    handleOffline() {
      this.isOffline = true;
    },
    handleVisibility() {
      if (!document.hidden && !this.ws.isConnected()) this.ws.reconnectNow();
    },

    onSocketConnect() {
      this.isOffline = false;
      if (this.currentProfile) {
        this.ws.subscribe(`/patient/${this.currentProfile.uid}`, this.onSocketChat);
        this.loadChats();
      }
      if (this.currentChatId > -1) this.scheduleReload();
    },

    onSocketDisconnect() {
      this.isOffline = true;
    },

    onSocketChat(frame) {
      let chat;
      try {
        chat = JSON.parse(frame.body);
      } catch (e) {
        console.error('[Chat] bad socket payload', e);
        return;
      }
      this.upsertChat(chat);
      if (chat.id === this.currentChatId) {
        // Мгновенно показываем сообщение из пуша (без ожидания дебаунса рефетча),
        // а хвостовой /messages добираем как сверку — на случай гонки нескольких
        // событий подряд или обновления seen.
        const incoming = (Array.isArray(chat.messages) ? chat.messages : []).filter(
          (m) => m && m.id != null
        );
        if (incoming.length) this.appendIncoming(incoming);
        this.scheduleReload();
      }
      this.maybePlaySound(chat);
    },

    appendIncoming(incoming) {
      const byId = new Map(this.messages.map((m) => [m.id, m]));
      let changed = false;
      incoming.forEach((m) => {
        const existing = byId.get(m.id);
        if (existing) Object.assign(existing, m);
        else this.messages.push(m);
        changed = true;
      });
      if (!changed) return;
      const wasAtBottom = this.isAtBottom();
      this.messages = sortByCreatedAsc(this.messages);
      this.reconcilePending();
      this.$nextTick(() => {
        if (wasAtBottom) this.scrollToBottom();
        else this.hasNewBelow = true;
      });
    },

    upsertChat(chat) {
      const i = this.chats.findIndex((c) => c.id === chat.id);
      if (i > -1) this.$set(this.chats, i, chat);
      else this.chats.push(chat);
    },

    // ---------- авторизация ----------
    async validateToken() {
      const token = storage.get(STORAGE_KEYS.TOKEN);
      const salt = storage.get(STORAGE_KEYS.SALT);
      if (!token || !salt) {
        storage.clear(STORAGE_KEYS.TOKEN, STORAGE_KEYS.SALT);
        return 'invalid';
      }
      try {
        const { data } = await api.get(`/token/${token}/${salt}`);
        if (data === false || data === 'false') return 'invalid';
        this.profiles = data;
        if (this.profiles.length === 1) this.currentProfile = this.profiles[0];
        this.loading = false;
        return 'ok';
      } catch (e) {
        console.error('[Auth] token validation failed', e);
        return 'network';
      }
    },

    getOrCreateSalt() {
      let salt = storage.get(STORAGE_KEYS.SALT);
      if (!salt || salt === 'null') {
        salt = Date.now().toString();
        storage.set(STORAGE_KEYS.SALT, salt);
      }
      return salt;
    },

    async auth() {
      const salt = this.getOrCreateSalt();
      if (this.code === null) await this.requestCode(salt);
      else await this.verifyCode(salt);
    },

    async requestCode(salt) {
      this.loadingPhone = true;
      try {
        const { data } = await api.get(`/code/${this.clearedPhone}/${salt}`);
        if (data === 200) {
          if (this.code === null) this.code = '';
          storage.set(STORAGE_KEYS.PHONE, this.phone);
          this.startResendCooldown();
        } else {
          this.error = 'Не удалось отправить код. Проверьте номер.';
        }
      } catch (e) {
        console.error('[Auth] request code failed', e);
        this.error = 'Нет связи с сервером. Попробуйте ещё раз.';
      } finally {
        this.loadingPhone = false;
      }
    },

    resendCode() {
      if (this.resendIn > 0) return;
      this.requestCode(this.getOrCreateSalt());
    },

    changeNumber() {
      this.code = null;
      this.resendIn = 0;
      if (this._resendTimer) clearInterval(this._resendTimer);
      this.clearError();
    },

    startResendCooldown() {
      this.resendIn = CONFIG.RESEND_COOLDOWN;
      if (this._resendTimer) clearInterval(this._resendTimer);
      this._resendTimer = setInterval(() => {
        this.resendIn -= 1;
        if (this.resendIn <= 0) clearInterval(this._resendTimer);
      }, 1000);
    },

    async verifyCode(salt) {
      this.loadingCode = true;
      try {
        const { data } = await api.get(`/code/${this.clearedPhone}/${this.code}/${salt}`);
        if (data === false || data === 'false') {
          this.error = 'Код не подходит. Проверьте SMS.';
          this.code = '';
        } else {
          this.clearError();
          storage.set(STORAGE_KEYS.TOKEN, data);
          const result = await this.validateToken();
          if (result === 'ok') this.ws.connect();
          else toast('Не удалось войти. Попробуйте ещё раз.', 'error');
        }
      } catch (e) {
        console.error('[Auth] verify code failed', e);
        this.error = 'Нет связи с сервером. Попробуйте ещё раз.';
      } finally {
        this.loadingCode = false;
      }
    },

    handlePhoneEnter() {
      if (this.clearedPhone.length === CONFIG.PHONE_LENGTH) this.auth();
    },

    confirmExit() {
      if (window.confirm('Выйти из чата? История переписки сохранится.')) this.exit();
    },

    exit() {
      storage.clear(STORAGE_KEYS.TOKEN, STORAGE_KEYS.PHONE, STORAGE_KEYS.SALT);
      this.code = null;
      this.loading = false;
      this.messages = [];
      this.pending = [];
      this.files = [];
      this.profiles = [];
      this.currentProfile = null;
      this.chats = [];
      this.currentChatId = -1;
      this.ws.disconnect();
      this.clearError();
    },

    clearError() {
      this.error = '';
    },

    // ---------- чаты и сообщения ----------
    async loadChats() {
      if (!this.currentProfile) return;
      try {
        const { data } = await api.get(`/${this.currentProfile.id}`);
        this.chats = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error('[Chat] load chats failed', e);
        toast('Не удалось обновить список чатов.', 'error');
      }
    },

    scheduleReload() {
      if (this._reloadTimer) {
        this._reloadQueued = true;
        return;
      }
      this._reloadTimer = setTimeout(() => {
        this._reloadTimer = null;
        this.loadMessages().then(() => {
          if (this._reloadQueued) {
            this._reloadQueued = false;
            this.scheduleReload();
          }
        });
      }, CONFIG.RELOAD_DEBOUNCE);
    },

    async loadMessages() {
      if (this.currentChatId === -1) return;
      this.isLoadingMessages = true;
      try {
        const { data } = await api.get(`/messages/${this.currentChatId}`);
        this.applyMessages(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('[Chat] load messages failed', e);
        toast('Не удалось загрузить сообщения.', 'error');
      } finally {
        this.isLoadingMessages = false;
      }
    },

    applyMessages(list) {
      const byId = new Map(this.messages.map((m) => [m.id, m]));
      const merged = sortByCreatedAsc(list).map((m) => {
        const existing = byId.get(m.id);
        return existing ? Object.assign(existing, m) : m;
      });
      const wasAtBottom = this.isAtBottom();
      this.messages = merged;
      this.reconcilePending();
      this.$nextTick(() => {
        if (wasAtBottom) this.scrollToBottom();
        else this.hasNewBelow = true;
      });
    },

    selectChat(chat) {
      if (this.currentChatId === chat.id) {
        this.clearChat();
        return;
      }
      this.clearChat();
      this.currentChatId = chat.id;
      this.hasNewBelow = false;
      this.loadMessages().then(() => this.$nextTick(() => this.scrollToBottom()));
    },

    clearChat() {
      this.currentChatId = -1;
      this.messages = [];
      this.pending = [];
      this.messagesSeen = [];
      this.files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
      this.files = [];
      this.message = '';
      this.hasNewBelow = false;
      this.resetTextarea();
    },

    selectProfile(profile) {
      if (this.currentProfile && this.currentProfile.id === profile.id) return;
      this.currentProfile = profile;
    },

    isProfileSelected(profile) {
      return !!this.currentProfile && this.currentProfile.id === profile.id;
    },

    // ---------- отправка ----------
    async send() {
      if (!this.canSend) return;
      this.isSending = true;
      const text = this.message.trim();
      const attached = this.files.slice();
      try {
        if (attached.length) await this.sendWithFiles(text, attached);
        else this.sendText(text);
      } finally {
        this.isSending = false;
      }
    },

    sendText(text) {
      if (!this.currentProfile) {
        toast('Выберите пациента.', 'error');
        return;
      }
      const entry = this.makePending(text, []);
      this.pending.push(entry);
      this.message = '';
      this.resetTextarea();
      this.$nextTick(() => this.scrollToBottom(true));

      const ok = this.ws.send(
        '/app/v2/chat',
        JSON.stringify([this.currentChatId, this.currentProfile.id, text])
      );
      if (!ok) {
        this.failPending(entry, 'Нет связи. Сообщение не отправлено.');
        return;
      }
      entry.timer = setTimeout(() => {
        if (!this.isPendingMatched(entry)) this.failPending(entry, null);
      }, CONFIG.SEND_TIMEOUT);
    },

    async sendWithFiles(text, attached) {
      if (!this.currentProfile) {
        toast('Выберите пациента.', 'error');
        return;
      }
      const entry = this.makePending(text, attached);
      entry._files = attached;
      this.pending.push(entry);
      this.message = '';
      this.files = [];
      this.resetTextarea();
      this.$nextTick(() => this.scrollToBottom(true));

      const form = new FormData();
      // Раздельные поля вместо messageData-массива: FormData склеивает массив через
      // запятую, и текст с запятой ломал разбор на бэкенде.
      form.append('chatId', this.currentChatId);
      form.append('userId', this.currentProfile.id);
      form.append('content', text);
      attached.forEach((f) => form.append('files', f.data));

      this.uploadPercent = 0;
      try {
        await uploadApi.post('/media/upload', form, {
          onUploadProgress: (e) => {
            if (e.total) this.uploadPercent = Math.round((e.loaded / e.total) * 100);
          },
        });
        entry.timer = setTimeout(() => {
          if (!this.isPendingMatched(entry)) this.failPending(entry, null);
        }, CONFIG.SEND_TIMEOUT);
      } catch (err) {
        console.error('[Chat] file upload failed', err);
        this.failPending(entry, 'Не удалось отправить файл. Нажмите, чтобы повторить.');
      } finally {
        this.uploadPercent = -1;
      }
    },

    makePending(content, attached) {
      return {
        localId: ++uidSeq,
        remark: '',
        content,
        created: new Date().toISOString(),
        media: attached.map((f) => ({ name: f.name, size: f.size, type: f.type })),
        seen: [],
        user: {
          id: this.currentProfile.id,
          name: this.currentProfile.name,
          type: 'patient',
        },
        status: 'sending',
        timer: null,
      };
    },

    isPendingMatched(entry) {
      const mine = this.currentProfile ? this.currentProfile.id : null;
      return this.messages.some((m) => {
        if (!m.user || m.user.id !== mine) return false;
        const sameText = (m.content || '') === (entry.content || '');
        const sameFiles = (m.media || []).length === (entry.media || []).length;
        const close =
          Math.abs(new Date(m.created).getTime() - new Date(entry.created).getTime()) < 10 * 60 * 1000;
        return sameText && sameFiles && close;
      });
    },

    reconcilePending() {
      this.pending = this.pending.filter((p) => {
        if (this.isPendingMatched(p)) {
          if (p.timer) clearTimeout(p.timer);
          (p._files || []).forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
          return false;
        }
        return true;
      });
    },

    failPending(entry, msg) {
      entry.status = 'error';
      if (entry.timer) {
        clearTimeout(entry.timer);
        entry.timer = null;
      }
      if (msg) toast(msg, 'error');
    },

    retryPending(message) {
      const entry = this.pending.find((p) => p.localId === message.localId);
      if (!entry) return;
      entry.status = 'sending';
      entry.created = new Date().toISOString();
      if (entry._files && entry._files.length) {
        this.pending = this.pending.filter((p) => p !== entry);
        this.files = entry._files;
        this.sendWithFiles(entry.content, entry._files);
      } else {
        const ok = this.ws.send(
          '/app/v2/chat',
          JSON.stringify([this.currentChatId, this.currentProfile.id, entry.content])
        );
        if (!ok) {
          this.failPending(entry, 'Нет связи. Сообщение не отправлено.');
          return;
        }
        entry.timer = setTimeout(() => {
          if (!this.isPendingMatched(entry)) this.failPending(entry, null);
        }, CONFIG.SEND_TIMEOUT);
      }
    },

    // ---------- вложения ----------
    onFileInput(event) {
      this.addFiles(Array.from(event.target.files || []));
      event.target.value = '';
    },

    addFiles(fileList) {
      let rejected = 0;
      fileList.forEach((file) => {
        if (file.size > MAX_FILE_SIZE) {
          rejected += 1;
          return;
        }
        if (this.files.some((f) => f.name === file.name && f.size === file.size)) return;
        const entry = {
          uid: `f${++uidSeq}`,
          data: file,
          name: file.name,
          size: file.size,
          type: file.type,
          preview: null,
        };
        if (file.type && file.type.startsWith('image/')) {
          entry.preview = URL.createObjectURL(file);
        }
        this.files.push(entry);
      });
      if (rejected > 0) {
        toast(`Файл больше ${MAX_FILE_SIZE_LABEL} — не прикреплён (${rejected}).`, 'error');
      }
    },

    removeFile(index) {
      const [removed] = this.files.splice(index, 1);
      if (removed && removed.preview) URL.revokeObjectURL(removed.preview);
    },

    // ---------- прочитано ----------
    markSeen(message) {
      if (!this.currentProfile || message.localId || message.id == null) return;
      const mine = this.currentProfile.id;
      const alreadySeen = (message.seen || []).some((u) => u.id === mine);
      if (alreadySeen || this.messagesSeen.includes(message.id)) return;
      this.messagesSeen.push(message.id);
      this.ws.send('/app/seen', JSON.stringify({ userId: mine, messageId: message.id }));
    },

    // ---------- скролл ----------
    onMessagesScroll() {
      if (this.isAtBottom()) this.hasNewBelow = false;
    },

    isAtBottom() {
      const el = this.$refs.messages;
      if (!el) return true;
      return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    },

    scrollToBottom(force) {
      const el = this.$refs.messages;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
      if (force) this.hasNewBelow = false;
    },

    // ---------- ввод ----------
    autogrow() {
      const el = this.$refs.input;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
    },

    resetTextarea() {
      this.$nextTick(() => {
        const el = this.$refs.input;
        if (el) el.style.height = 'auto';
      });
    },

    // ---------- звук ----------
    maybePlaySound(chat) {
      const last = chat && chat.messages && chat.messages[0];
      if (!last || !last.user || !this.currentProfile) return;
      if (last.user.id === this.currentProfile.id) return;
      const now = Date.now();
      if (now - this._lastSoundAt < CONFIG.SOUND_THROTTLE) return;
      this._lastSoundAt = now;
      try {
        if (!this._sound) this._sound = new Audio(require('@/assets/bb.mp3'));
        this._sound.currentTime = 0;
        this._sound.play().catch(() => {});
      } catch (e) {
        /* автоплей заблокирован */
      }
    },
  },
};
</script>

<style>
/* ============ токены (не scoped) ============ */
/* Фирменный стиль «Садко» с поправкой на удобство: бирюза #0B89AC,
   тёмно-синий из шапки #112F57, светлый блок #E9F8FF, шрифт Montserrat. */
:root {
  --app-bg: #f2f6f8;
  --surface: #ffffff;
  --surface-2: #eef6fa;
  --tint: #e9f8ff;
  --border: #e3e8ec;
  --border-strong: #d2dde3;
  --text: #1f2733;
  --text-muted: #6b7681;
  --heading: #143a63;
  --accent: #0b89ac;
  --accent-hover: #097b9a;
  --accent-soft: #d7eef5;
  --accent-bright: #64bffe;
  --brand-gradient: linear-gradient(135deg, #2ab6b0 0%, #0b89ac 55%, #007db0 100%);
  --bubble-in: #eef4f7;
  --bubble-in-border: #e0e8ec;
  --bubble-in-text: #1f2733;
  --bubble-out: #0b89ac;
  --bubble-out-text: #ffffff;
  --danger: #c8352f;
  --font: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  --radius: 12px;
  --sidebar-w: 340px;
}

@media (prefers-color-scheme: dark) {
  :root {
    --app-bg: #0b1622;
    --surface: #10202f;
    --surface-2: #16293b;
    --tint: #16293b;
    --border: #223648;
    --border-strong: #2c4258;
    --text: #e6ecf2;
    --text-muted: #9bacba;
    --heading: #bcd7ec;
    --accent: #3aa8cc;
    --accent-hover: #64bffe;
    --accent-soft: #16303f;
    --accent-bright: #64bffe;
    --bubble-in: #1a2c3d;
    --bubble-in-border: #263a4c;
    --bubble-in-text: #e6ecf2;
    --bubble-out: #0b6f8b;
    --bubble-out-text: #ffffff;
    --danger: #e2726c;
  }
}

* {
  box-sizing: border-box;
}

html,
body {
  margin: 0;
  background: var(--app-bg);
}

html,
body,
#app,
.app {
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
  overflow: hidden;
}

.app {
  background: var(--app-bg);
  color: var(--text);
  font-family: var(--font);
  font-size: 15px;
  line-height: 1.45;
  -webkit-font-smoothing: antialiased;
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.001ms !important;
    transition-duration: 0.001ms !important;
  }
}
</style>

<style scoped>
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* ============ типографика утилит ============ */
.lbl {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-muted);
  margin-bottom: 6px;
}

.lbl--section {
  padding: 16px 4px 8px;
  margin: 0;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* ============ кнопки ============ */
.btn {
  font: inherit;
  border: none;
  border-radius: var(--radius);
  padding: 13px 18px;
  cursor: pointer;
}

.btn--primary {
  display: block;
  width: 100%;
  background: var(--brand-gradient);
  color: #fff;
  font-weight: 600;
  font-size: 15px;
  box-shadow: 0 6px 16px rgba(11, 137, 172, 0.28);
  transition: filter 0.15s ease, box-shadow 0.15s ease;
}

.btn--primary:hover:not(:disabled) {
  filter: brightness(1.05);
}

.btn--primary:disabled {
  background: var(--border-strong);
  color: var(--text-muted);
  box-shadow: none;
  cursor: not-allowed;
}

.linkbtn {
  background: none;
  border: none;
  color: var(--accent);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.linkbtn:hover:not(:disabled) {
  color: var(--accent-hover);
}

.linkbtn:disabled {
  color: var(--text-muted);
  cursor: default;
}

.ghost-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: none;
  border-radius: 10px;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.ghost-btn:hover {
  background: var(--surface-2);
  color: var(--accent);
}

.ghost-btn.is-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

/* ============ поля ввода ============ */
.input {
  width: 100%;
  border: 1px solid var(--border-strong);
  border-radius: 10px;
  padding: 12px 13px;
  font: inherit;
  font-size: 16px;
  color: var(--text);
  background: var(--surface);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.input::placeholder {
  color: var(--text-muted);
}

.input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(11, 137, 172, 0.14);
}

.input:disabled {
  background: var(--surface-2);
  color: var(--text-muted);
}

/* ============ каркас ============ */
.login-wrap {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
  background:
    radial-gradient(120% 90% at 50% -10%, rgba(11, 137, 172, 0.12), transparent 60%),
    var(--app-bg);
}

.login {
  width: 100%;
  max-width: 380px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 30px 26px;
  box-shadow: 0 16px 48px rgba(20, 58, 99, 0.12);
}

.login__logo {
  display: block;
  height: 40px;
  margin: 0 auto 16px;
}

.login__title {
  margin: 0 0 6px;
  font-size: 21px;
  font-weight: 700;
  text-align: center;
  color: var(--heading);
}

.login__hint {
  margin: 0 0 20px;
  color: var(--text-muted);
  text-align: center;
  font-size: 14px;
  line-height: 1.5;
}

.login__error {
  margin: 0 0 12px;
  color: var(--danger);
  text-align: center;
  font-size: 14px;
}

.login .btn--primary {
  margin-top: 8px;
}

.pin {
  margin: 14px 0 4px;
  text-align: center;
}

.pin__actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  margin-top: 10px;
}

.login__about {
  display: block;
  margin: 14px auto 0;
}

.login__info {
  margin-top: 12px;
}

.app-loading {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: var(--text-muted);
  font-size: 14px;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2.5px solid var(--border-strong);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;
}

.spinner--lg {
  width: 34px;
  height: 34px;
  border-width: 3px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.layout {
  height: 100%;
  display: flex;
}

/* ============ сайдбар ============ */
.sidebar {
  width: var(--sidebar-w);
  min-width: var(--sidebar-w);
  background: var(--surface);
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
}

.sidebar__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 0 16px;
  border-bottom: 1px solid var(--border);
}

.sidebar__logo {
  height: 26px;
}

.sidebar__scroll {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 8px;
}

.profiles {
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border);
}

.profile {
  display: block;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: none;
  border-left: 3px solid transparent;
  border-radius: 8px;
  background: none;
  color: var(--text);
  font: inherit;
  font-size: 15px;
  cursor: pointer;
}

.profile:hover {
  background: var(--surface-2);
}

.profile--active {
  background: var(--surface-2);
  border-left-color: var(--accent);
  font-weight: 600;
  color: var(--heading);
}

.search {
  position: relative;
  margin-top: 4px;
}

.search__icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: var(--text-muted);
  pointer-events: none;
}

.input--search {
  font-size: 14px;
  padding: 9px 12px 9px 34px;
  border-radius: 9px;
  background: var(--surface-2);
  border-color: transparent;
}

.chat-list {
  padding-bottom: 8px;
}

.chat-list__empty {
  color: var(--text-muted);
  text-align: center;
  font-size: 14px;
  margin-top: 28px;
}

.sidebar__foot {
  border-top: 1px solid var(--border);
  padding: 10px 16px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.conn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--danger);
}

.conn__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  animation: blink 1.6s ease-in-out infinite;
}

@keyframes blink {
  50% {
    opacity: 0.3;
  }
}

.sidebar__test {
  font-size: 11px;
  color: var(--text-muted);
}

/* ============ аватар ============ */
.avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--accent-soft);
  color: var(--accent);
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-transform: uppercase;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* ============ область чата ============ */
.chat {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: var(--app-bg);
}

.chat--empty {
  align-items: center;
  justify-content: center;
}

.empty {
  text-align: center;
  color: var(--text-muted);
  font-size: 14px;
}

.empty__title {
  font-size: 17px;
  font-weight: 600;
  color: var(--heading);
  margin: 0 0 4px;
}

.chat__head {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 60px;
  padding: 0 12px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}

.chat__back {
  display: none;
  margin-left: -4px;
}

.chat__meta {
  min-width: 0;
  flex: 1;
}

.chat__name {
  font-size: 16px;
  font-weight: 600;
  color: var(--heading);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat__status {
  font-size: 12.5px;
  color: var(--text-muted);
}

.chat__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  position: relative;
}

.messages {
  flex: 1;
  overflow-y: auto;
  padding: 14px 14px 8px;
  display: flex;
  flex-direction: column;
}

.messages__empty {
  margin: auto;
  max-width: 300px;
  text-align: center;
  color: var(--text-muted);
  font-size: 14px;
}

.day {
  text-align: center;
  margin: 12px 0 6px;
}

.day span {
  display: inline-block;
  background: var(--surface-2);
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 500;
  padding: 3px 12px;
  border-radius: 999px;
}

.jump {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 88px;
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 7px 14px;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(20, 58, 99, 0.22);
}

/* ============ композер ============ */
.composer {
  background: var(--surface);
  border-top: 1px solid var(--border);
  padding: 8px 10px calc(8px + env(safe-area-inset-bottom));
}

.composer--disabled {
  opacity: 0.65;
}

.composer__files {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 4px 8px;
}

.attach {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 5px 6px;
  max-width: 220px;
}

.attach__thumb {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  object-fit: cover;
}

.attach__ext {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: var(--accent);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.attach__meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.attach__name {
  font-size: 12.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.attach__size {
  font-size: 11px;
  color: var(--text-muted);
}

.attach__x {
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 17px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
}

.composer__row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

.composer__input {
  flex: 1;
  resize: none;
  border: 1px solid var(--border-strong);
  border-radius: 20px;
  padding: 10px 14px;
  font: inherit;
  font-size: 16px;
  line-height: 1.35;
  background: var(--surface);
  color: var(--text);
  max-height: 132px;
}

.composer__input:focus {
  outline: none;
  border-color: var(--accent);
}

.composer__input:disabled {
  background: var(--surface-2);
}

.send-btn {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background: var(--accent);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, transform 0.1s ease;
}

.send-btn:hover:not(:disabled) {
  background: var(--accent-hover);
}

.send-btn:active:not(:disabled) {
  transform: scale(0.92);
}

.send-btn:disabled {
  background: var(--border-strong);
  color: var(--text-muted);
  cursor: not-allowed;
}

.send-btn__pct {
  font-size: 11px;
  font-weight: 700;
}

/* ============ адаптив ============ */
@media (max-width: 860px) {
  :root {
    --sidebar-w: 100%;
  }

  .sidebar {
    min-width: 100%;
    border-right: none;
  }

  .is-hidden-mobile {
    display: none !important;
  }

  .chat__back {
    display: inline-flex;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
